/**
 * ResetPasswordPage - Handles the Supabase password reset callback.
 *
 * When a user clicks the reset link in their email, Supabase redirects here
 * with a valid PASSWORD_RECOVERY session. This page lets them set a new password.
 */
import { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import Logo from '../components/logo/Logo';
import Spinner from '../components/Spinner';
import { AppContext } from '../components/AppContext.tsx';
import { useCrypto } from '../hooks/useCrypto.ts';
import {
  generateRecoveryKey,
  encryptMasterKeyForRecovery,
} from '../lib/crypto';

interface Errors {
  form?: string | null;
  password?: string | null;
  confirmPassword?: string | null;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { supabaseClient, authSession, authLoading } = useContext(AppContext);
  const { unlock, getMasterKey } = useCrypto();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [newRecoveryKey, setNewRecoveryKey] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'recovery-key'>('form');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Errors = {};
    if (newPassword.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const { error: updateError } = await supabaseClient.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setErrors({ form: updateError.message });
        return;
      }

      // Derive and store the masterKey from the new password
      await unlock(newPassword, authSession.user.email!);

      // Generate a new recovery key
      const { displayKey, rawKey } = generateRecoveryKey();

      // Encrypt masterKey for recovery storage
      const masterKey = getMasterKey();
      if (masterKey) {
        const encryptedMasterKey = await encryptMasterKeyForRecovery(masterKey, rawKey);

        // Store encrypted masterKey on server
        fetch(`${import.meta.env.VITE_API_URL}/api/recovery-keys`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession.access_token}`,
          },
          body: JSON.stringify({ encrypted_master_key: encryptedMasterKey }),
        }).catch(err => {
          console.warn('Failed to store recovery key on server:', err);
        });
      }

      toast.success('Password updated successfully');
      setNewRecoveryKey(displayKey);
      setPhase('recovery-key');
    } catch {
      setErrors({ form: 'Failed to update password. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [newPassword, confirmPassword, supabaseClient, authSession, unlock, getMasterKey]);

  // Still checking auth state - show loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page p-4">
        <Spinner size="md" />
      </div>
    );
  }

  // If there's no session after auth check completes, the reset link is invalid or expired
  if (!authSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page p-4">
        <div className="w-[300px]">
          <div className="text-center">
            <div className="h-[64px] mb-2 flex items-center justify-center">
              <Logo variant="wordmark" text="Consl" size={32} ariaLabel="Consl" />
            </div>
            <h1 className="text-2xl font-normal text-primary mb-4">
              Link Expired
            </h1>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-secondary">
              This reset link is invalid or has expired. Please request a new one.
            </p>
            
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              Request New Reset Link
            </button>
            
            <div className="flex justify-start">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-secondary hover:text-primary transition-colors duration-150"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'recovery-key' && newRecoveryKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page p-4">
        <div className="w-[300px]">
          <div className="text-center">
            <div className="h-[64px] mb-2 flex items-center justify-center">
              <Logo variant="wordmark" text="Consl" size={32} ariaLabel="Consl" />
            </div>
            <h1 className="text-2xl font-normal text-primary mb-4">
              Save Your Recovery Key
            </h1>
          </div>

          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-bg-panel border border-border-default">
              <p className="text-sm text-secondary mb-2">
                Your new recovery key (save this!):
              </p>
              <p className="font-mono text-primary text-center tracking-wider select-all">
                {newRecoveryKey}
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
            >
              I've Saved My Recovery Key
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-page p-4">
      <div className="w-[300px]">
        {/* Header */}
        <div className="text-center">
          <div className="h-[64px] mb-2 flex items-center justify-center">
            <Logo variant="wordmark" text="Consl" size={32} ariaLabel="Consl" />
          </div>
          <h1 className="text-2xl font-normal text-primary mb-4">
            Set New Password
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.form && (
            <p className="text-xs text-error">{errors.form}</p>
          )}

          <div>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setErrors(prev => ({ ...prev, password: null })); }}
              placeholder="New password (8+ characters)"
              disabled={loading}
              className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.password ? 'border-error' : 'border-border-default'}`}
              autoFocus
            />
            {errors.password && <p className="text-xs text-error mt-1">{errors.password}</p>}
          </div>

          <div>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: null })); }}
              placeholder="Confirm new password"
              disabled={loading}
              className={`w-full h-[42px] px-4 border rounded-lg bg-bg-input text-primary text-base md:text-sm focus:outline-none focus:border-accent transition-[border-color] duration-150 ${errors.confirmPassword ? 'border-error' : 'border-border-default'}`}
            />
            {errors.confirmPassword && <p className="text-xs text-error mt-1">{errors.confirmPassword}</p>}
          </div>

          <button
            type="submit"
            disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
            className="w-full h-[42px] bg-accent text-tertiary rounded-lg hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center justify-center active:scale-[0.97]"
          >
            {loading ? <Spinner size="sm" color="text-tertiary" /> : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
